import React, { useCallback, useEffect, useState, useMemo } from "react";
import GoogleMapRoute from "modules/trips/components/Summary/GoogleMapRoute";
import useTrips from "hooks/useGetLiveTrips";
import useClients from "hooks/useGetClients";
import useTruckTypes from "hooks/useGetTruckTypes";
import useMQTT from "hooks/useMQTT";
import styles from "./LiveTracking.module.css";
import FilterBar from "./components/Filter";
import CardsContainer from "./components/cardsContainer";

const CONNECTED_THRESHOLD = 2 * 60 * 1000;
const INACTIVE_THRESHOLD = 25 * 60 * 1000;

function LiveTracking() {
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedTrip, setSelectedTrip] = useState(null);
    const [mqttLiveTripsData, setMqttLiveTripsData] = useState({});
    const [mapView, setMapView] = useState({
        center: { lat: 51.5074, lng: -0.1278 },
        zoom: 8,
    });
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [clientInputValue, setClientInputValue] = useState("");
    const [truckInputValue, setTruckInputValue] = useState("");
    const [selectedFilters, setSelectedFilters] = useState({});

    const clients = useClients(clientInputValue);
    const trucksList = useTruckTypes(truckInputValue);

    const { trips, drivers, truckTypes, tripPoints, trucks, totalRecords } = useTrips(
        currentPage,
        selectedFilters,
        debouncedSearch
    );

    const liveTripsArray = useMemo(() => Object.values(mqttLiveTripsData), [mqttLiveTripsData]);

    const memoizedFleetData = useMemo(
        () => ({
            trips,
            drivers,
            truckTypes,
            tripPoints,
            trucks,
        }),
        [trips, drivers, truckTypes, tripPoints, trucks]
    );

    const activeTruckIds = useMemo(() => trips.map((trip) => trip.id).filter(Boolean), [trips]);

    const topics = useMemo(
        () => activeTruckIds.map((tid) => `illa/fleet/location/vehicle/${tid}`),
        [activeTruckIds]
    );

    useEffect(() => {
        const timerId = setTimeout(() => {
            setDebouncedSearch(search);
        }, 500);

        return () => {
            clearTimeout(timerId);
        };
    }, [search]);

    useEffect(() => {
        const activeTruckSet = new Set(activeTruckIds);
        if (Object.keys(mqttLiveTripsData).length > 0) {
            setMqttLiveTripsData((prevData) => {
                const hasInactiveTrucks = Object.keys(prevData).some(
                    (id) => !activeTruckSet.has(id)
                );
                if (!hasInactiveTrucks) return prevData;

                return Object.fromEntries(
                    Object.entries(prevData).filter(([id]) => activeTruckSet.has(id))
                );
            });
        }
        if (selectedTrip && !activeTruckSet.has(selectedTrip.tid)) {
            setSelectedTrip(null);
        }
    }, [activeTruckIds, selectedTrip]);

    const handleMqttMessage = useCallback((topic, message) => {
        try {
            const tid = topic.split("/").pop();
            const data = JSON.parse(message.toString());

            setMqttLiveTripsData((prev) => {
                const existingVehicle = prev[tid];

                if (!existingVehicle) {
                    return {
                        ...prev,
                        [tid]: {
                            tid,
                            ...data,
                            connection: "connected",
                        },
                    };
                }

                // Prevent unnecessary state updates
                if (
                    existingVehicle.connection === "connected" &&
                    existingVehicle.loc.lat === data.loc.lat &&
                    existingVehicle.loc.lon === data.loc.lon
                ) {
                    return prev;
                }

                return {
                    ...prev,
                    [tid]: {
                        ...existingVehicle,
                        ...data,
                        connection: "connected",
                    },
                };
            });
        } catch (error) {
            console.error("Error processing MQTT message:", error);
        }
    }, []);

    // const handleMqttMessage = useCallback((topic, message) => {
    //     try {
    //         const tid = topic.split("/").pop();
    //         const data = JSON.parse(message.toString());
    //         const now = Date.now();

    //         setMqttLiveTripsData((prev) => {
    //             const existingVehicle = prev[tid];
    //             const lastTimestamp = existingVehicle?.loc?.ts || now - 60000;
    //             const timeSinceUpdate = now - lastTimestamp;

    //             let newStatus;
    //             if (timeSinceUpdate > INACTIVE_THRESHOLD) {
    //                 newStatus = "inactive";
    //             } else if (timeSinceUpdate > CONNECTED_THRESHOLD) {
    //                 newStatus = "unconnected";
    //             } else {
    //                 newStatus = "connected";
    //             }

    //             let minutesSinceLastConnection = "";
    //             if (newStatus !== "connected") {
    //                 const totalMinutes = Math.floor(timeSinceUpdate / 60000);
    //                 const hours = Math.floor(totalMinutes / 60);
    //                 const minutes = totalMinutes % 60;
    //                 minutesSinceLastConnection =
    //                     hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
    //             }

    //             if (!existingVehicle) {
    //                 return {
    //                     ...prev,
    //                     [tid]: {
    //                         tid,
    //                         ...data,
    //                         connection: newStatus,
    //                         minutesSinceLastConnection,
    //                     },
    //                 };
    //             }

    //             // Prevent unnecessary state updates
    //             if (
    //                 existingVehicle.connection === newStatus &&
    //                 existingVehicle.minutesSinceLastConnection === minutesSinceLastConnection &&
    //                 existingVehicle.loc.lat === data.loc.lat &&
    //                 existingVehicle.loc.lon === data.loc.lon
    //             ) {
    //                 return prev;
    //             }

    //             return {
    //                 ...prev,
    //                 [tid]: {
    //                     ...existingVehicle,
    //                     ...data,
    //                     connection: newStatus,
    //                     minutesSinceLastConnection,
    //                 },
    //             };
    //         });
    //     } catch (error) {
    //         console.error("Error processing MQTT message:", error);
    //     }
    // }, []);

    useMQTT(topics, handleMqttMessage);

    return (
        <div className={styles.liveTrackingContainer}>
            <FilterBar
                setSearch={setSearch}
                setSelectedFilters={setSelectedFilters}
                search={search}
                selectedFilters={selectedFilters}
                clients={clients}
                trucksList={trucksList}
                setClientInputValue={setClientInputValue}
                setTruckInputValue={setTruckInputValue}
                clientInputValue={clientInputValue}
                truckInputValue={truckInputValue}
            />
            <GoogleMapRoute
                containerElement={{ height: "90vh", width: "100%" }}
                liveTrackingTrips={liveTripsArray}
                setSelectedTrip={setSelectedTrip}
                selectedTrip={selectedTrip}
                allActiveTrips={trips}
                mapCenter={mapView.center}
                mapZoom={mapView.zoom}
                setMapView={setMapView}
            />
            <CardsContainer
                allTrips={memoizedFleetData}
                liveTrackingTrips={liveTripsArray}
                currentPage={currentPage}
                totalRecords={totalRecords}
                setCurrentPage={setCurrentPage}
                setSelectedTrip={setSelectedTrip}
                selectedTrip={selectedTrip}
            />
        </div>
    );
}

export default React.memo(LiveTracking);
